import { useEffect, useEffectEvent, useMemo } from 'react';

import { usePathname } from 'expo-router';
import { useEnableLoyaltyServiceOffers } from 'features/offer/hooks/use-enable-loyalty-service-offers';
import { useEvaluateLoyaltyServiceOffers } from 'features/offer/hooks/use-evaluate-loyalty-service-offers';
import { buildAppliedOfferServiceInputs } from 'features/offer/utils/build-applied-offer-service-inputs';
import { useStoreContext } from 'features/store';
import { usePosDataQuery } from 'features/store/hooks/use-pos-data-query';

import { ILegacyCartEntry } from '@rbi-ctg/menu';
import { useCart } from 'hooks/use-cart';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import { useLoyaltyRewardsList } from 'hooks/use-loyalty-rewards-list';
import { routes } from 'navigation/routing';
import { selectors, useAppSelector } from 'state/global-state';
import { useLoyaltyContext } from 'state/loyalty';
import { useLoyaltyOfferAvailabilityEvaluation } from 'state/loyalty/offers/hooks/use-loyalty-offer-availability-evaluation';
import { LoyaltyAppliedOffer, LoyaltyReward } from 'state/loyalty/types';
import { useMainMenuContext } from 'state/menu/main-menu';
import { getServiceModeFromLS } from 'state/service-mode';
import { useDerivedServiceModeContext } from 'state/service-mode/derived';
import { ServiceMode } from 'state/service-mode/types';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { useMenuQueryOptions } from 'state/unified-menu/use-menu-query-options';
import { enrichLegacyCartEntryFromMenu } from 'state/unified-menu/utils';
import { getCartEntryAvailabilityOneMenu, getUnavailableCartEntries } from 'utils/availability';
import logger from 'utils/logger';

interface UseCartAvailabilityCheckParams {
  cartEntries: ILegacyCartEntry[];
  isStoreChangeHydrationInProgress: boolean;
  appliedOffers: LoyaltyAppliedOffer[];
  loyaltyUserId?: string | null;
}

/**
 * React hook that validates cart entry availability when the user changes stores.
 *
 * This checks for cart entries availability for a new selected store and service mode
 * during hydration/rehydration flows. It supports both One Menu and Legacy store types, applying
 * different availability checks accordingly:
 *
 * - **One Menu**:
 *   - Waits until One Menu data (main menu, indexed menu) is fully loaded.
 *   - Availabity check to filter out unavailable entries based on
 *     the indexed menu, service mode, and active day parts.
 *
 * - **Legacy**:
 *   - Fetches POS pricing data for the store using `refetchPosData`.
 *   - If no POS data is available, all entries are considered unavailable.
 *
 * When unavailable entries are found:
 * - Sets confirmation parameters with `setOnConfirmStoreChangeParams`.
 * - Provides a callback to remove the unavailable entries from the cart.
 * - Triggers the "not available" modal to inform the user.
 *
 * @param {UseCartAvailabilityCheckParams} params - Hook parameters.
 * @param {ILegacyCartEntry[]} params.cartEntries - The list of cart entries to check for availability.
 * @param {boolean} params.isStoreChangeHydrationInProgress - Indicates if store-change hydration is in progress.
 *
 * @returns {void}
 */

export const useCartAvailabilityCheck = ({
  cartEntries,
  isStoreChangeHydrationInProgress,
  appliedOffers,
  loyaltyUserId,
}: UseCartAvailabilityCheckParams) => {
  const { removeAllFromCart } = useCart();
  const { store, setOnConfirmStoreChangeParams } = useStoreContext();
  const { posServiceMode } = useMenuQueryOptions();
  const {
    isLoading: isOneMenuLoading,
    mainMenu,
    indexedMenu,
    loadedStoreNumber,
    hasError: unifiedMenuHasError,
  } = useUnifiedMenuContext();
  const { activeDayParts, isDayPartsSourceResolved } = useMainMenuContext();
  const { isDelivery } = useDerivedServiceModeContext();
  const { isOfferUnavailable, findCmsOfferByCmsId } = useLoyaltyOfferAvailabilityEvaluation();
  const { evaluateLoyaltyUserOffers } = useLoyaltyContext();
  const { allRewards } = useLoyaltyRewardsList();

  const allRewardsMap = useMemo(() => {
    const dictionary: Record<string, LoyaltyReward> = {};
    (allRewards ?? []).forEach(item => (dictionary[item._id] = item));
    return dictionary;
  }, [allRewards]);

  const appliedLoyaltyRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);
  const { evaluateAndApplyOffersFeedback } = useEvaluateLoyaltyServiceOffers();
  const { enabled: loyaltyServiceOffersEnabled } = useEnableLoyaltyServiceOffers();

  const evaluateOffers = useEffectEvent(async () => {
    // Loyalty-service ON: validate applied offers against the loyalty service
    // instead of the legacy `LoyaltyUserOffers` engine query, mirroring the cart
    // seams (`useLoyaltyCartEntriesEvaluation` / `useCartIncentivesEvaluation`).
    if (loyaltyServiceOffersEnabled) {
      const { appliedIncentives, cartEntries: parsedEntries } = buildAppliedOfferServiceInputs({
        appliedOffers,
        cartEntries,
        appliedRewards: appliedLoyaltyRewards,
      });

      await evaluateAndApplyOffersFeedback({
        appliedIncentives,
        cartEntries: parsedEntries,
        serviceMode: getServiceModeFromLS(),
        storeId: store.number,
      });
      return;
    }

    evaluateLoyaltyUserOffers(loyaltyUserId ?? '', appliedOffers, cartEntries);
  });

  const isUnifiedMenuEnabled = useEnableUnifiedMenu();
  const pathname = usePathname();
  const isFromMenuScreen = pathname.startsWith(routes.menu);

  const { refetch: refetchPosData } = usePosDataQuery({
    lazy: true,
    storeNumber: '',
  });

  const isCurrentStoreMenuFetched = Boolean(store.number) && loadedStoreNumber === store.number;
  const isUnifiedMenuLoaded = isCurrentStoreMenuFetched && indexedMenu?.isLoaded();
  const shouldUseUnifiedMenuAvailability = isUnifiedMenuEnabled && isUnifiedMenuLoaded;
  const shouldWaitForUnifiedMenuAvailability =
    Boolean(store.number) &&
    isUnifiedMenuEnabled &&
    !unifiedMenuHasError &&
    !isCurrentStoreMenuFetched &&
    (isOneMenuLoading || loadedStoreNumber !== store.number);

  useEffect(() => {
    if (
      isStoreChangeHydrationInProgress ||
      !cartEntries.length ||
      !appliedOffers.length ||
      // We are already checking this in the cart with useLoyaltyCartEntriesEvaluation. We should do this check only for the menu screen.
      !isFromMenuScreen ||
      shouldWaitForUnifiedMenuAvailability
    ) {
      return;
    }
    evaluateOffers();
  }, [
    cartEntries,
    isStoreChangeHydrationInProgress,
    loyaltyUserId,
    appliedOffers,
    isFromMenuScreen,
    pathname,
    shouldWaitForUnifiedMenuAvailability,
    // evaluateOffers is an Effect Event and must stay out of dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]);

  // @TODO: Investigate how and when to trigger this as a manual function instead of an useEffect
  // The main issue with not running this as a side effect is that the price order mutation will be
  // triggered with the unavailable cartEntries.
  // We will need to wait to call the price order mutation after this check is done.
  useEffect(() => {
    // Do nothing if there are no cart entries to check,
    // store change hydration is running or the feature flag is disabled
    if ((!cartEntries.length && !appliedOffers.length) || isStoreChangeHydrationInProgress) {
      return;
    }

    const isDeliveryMode = isDelivery;
    const isOneMenuReady = !isOneMenuLoading && !!mainMenu.length && isUnifiedMenuLoaded;

    const removeUnavailableCartEntries = (
      unavailableCartEntries: ILegacyCartEntry[],
      unavailableChildrenCartEntries: ILegacyCartEntry[]
    ) => {
      if (!unavailableCartEntries.length) {
        return;
      }

      // Display the not available modal and remove the unavailable entries from the cart.
      setOnConfirmStoreChangeParams({
        callback: () => removeAllFromCart(unavailableCartEntries),
        newStore: store,
        unavailableCartEntries,
        unavailableChildrenCartEntries,
      });
    };

    // One Menu check
    if (shouldWaitForUnifiedMenuAvailability) {
      return;
    }

    if (shouldUseUnifiedMenuAvailability) {
      // Ensure we have all the necessary One Menu data to check for entries availability.
      if (!isOneMenuReady) {
        return;
      }

      // `activeDayParts` can be transiently empty while main-menu resolves which day-part
      // source to use - checking availability against it in that window would flag every item
      // as OUT_OF_DAYPART and empty the cart.
      if (!isDayPartsSourceResolved) {
        return;
      }

      const unavailableCartEntries: ILegacyCartEntry[] = [];
      const unavailableChildrenCartEntries: ILegacyCartEntry[] = [];

      cartEntries.forEach(entry => {
        const availability = getCartEntryAvailabilityOneMenu(
          entry,
          indexedMenu,
          posServiceMode,
          activeDayParts,
          Boolean(store.hasBurgersForBreakfast)
        );

        unavailableCartEntries.push(...availability.unavailableEntries);
        unavailableChildrenCartEntries.push(...availability.unavailableChildrenEntries);
      });

      appliedOffers.forEach(appliedOffer => {
        const cmsOffer = findCmsOfferByCmsId(appliedOffer.cmsId ?? '');
        if (!cmsOffer) {
          return;
        }

        if (isOfferUnavailable(cmsOffer)) {
          const unavailableEntry = cartEntries.find(entry => entry.cartId === appliedOffer.cartId);

          if (unavailableEntry) {
            unavailableCartEntries.push(unavailableEntry);
          }
        }
      });

      // `entry.name` is captured at add-to-cart time and can be stale after a language switch -
      // re-resolve it against the current locale before showing it in the unavailable-items modal.
      const enrichedUnavailableCartEntries = unavailableCartEntries.map(entry =>
        enrichLegacyCartEntryFromMenu(entry, indexedMenu, allRewardsMap)
      );
      const enrichedUnavailableChildrenCartEntries = unavailableChildrenCartEntries.map(entry =>
        enrichLegacyCartEntryFromMenu(entry, indexedMenu, allRewardsMap)
      );

      removeUnavailableCartEntries(
        enrichedUnavailableCartEntries,
        enrichedUnavailableChildrenCartEntries
      );
      return;
    }

    // A One Menu (Brink) store must never fall through to the legacy PLU-pricing check below -
    // that pipeline doesn't serve One Menu stores, so it would flag every cart entry unavailable.
    if (isUnifiedMenuEnabled) {
      return;
    }

    // Legacy check
    const getUnavailableCartEntriesLegacy = async (): Promise<{
      unavailableCartEntries: ILegacyCartEntry[];
      unavailableChildrenCartEntries: ILegacyCartEntry[];
    }> => {
      const selectedStorePrices = await refetchPosData({
        storeNumber: store.number ?? '',
        isDelivery: isDeliveryMode,
      });

      if (!selectedStorePrices) {
        logger.warn({
          message: 'PLU data for store is null. Flagging all cart entries as unavailable.',
          storeNumber: store.number,
          storeId: store._id,
          posVendor: store.posVendor,
        });

        // If we don't have posData information, then we mark all the cart entries as not available.
        return { unavailableCartEntries: cartEntries, unavailableChildrenCartEntries: [] };
      }

      const { unavailableCartEntries, unavailableChildrenCartEntries } = getUnavailableCartEntries(
        cartEntries,
        store,
        selectedStorePrices.posData,
        (getServiceModeFromLS() ?? ServiceMode.TAKEOUT) as ServiceMode
      );

      return {
        unavailableCartEntries,
        unavailableChildrenCartEntries,
      };
    };

    const runLegacyAvailabilityCheck = async () => {
      const {
        unavailableCartEntries,
        unavailableChildrenCartEntries,
      } = await getUnavailableCartEntriesLegacy();
      removeUnavailableCartEntries(unavailableCartEntries, unavailableChildrenCartEntries);
    };

    runLegacyAvailabilityCheck();
  }, [
    appliedOffers,
    isOfferUnavailable,
    findCmsOfferByCmsId,
    isOneMenuLoading,
    mainMenu.length,
    indexedMenu,
    posServiceMode,
    refetchPosData,
    isDelivery,
    store,
    setOnConfirmStoreChangeParams,
    removeAllFromCart,
    isStoreChangeHydrationInProgress,
    isUnifiedMenuLoaded,
    shouldUseUnifiedMenuAvailability,
    shouldWaitForUnifiedMenuAvailability,
    cartEntries,
    activeDayParts,
    isUnifiedMenuEnabled,
    allRewardsMap,
    isDayPartsSourceResolved,
  ]);
};
